import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext.jsx";
import { getEmpresas, createUser, updateUser } from "../../api/api";
import { getGruposTrabajo } from "../../api/gruposTrabajo";
import { getJornadas } from "../../api/jornadas";
import { isValidDNI, isValidPhone } from "../../utils/validators";

const validarEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validarNombre = (nombre) => /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s\-']+$/.test(nombre);
const validarPassword = (pwd) => pwd.length >= 6;

export default function UserFormModal({ user = {}, onClose, onSaved, empresaId = null }) {
  const { token, user: me, reloadUser } = useAuth();
  const isEdit = !!user._id;

  const [empresas, setEmpresas] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [jornadasList, setJornadasList] = useState([]);
  const [jornadasDropdownOpen, setJornadasDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [form, setForm] = useState({
    nombre: user.nombre || "",
    segundoNombre: user.segundoNombre || "",
    primerApellido: user.primerApellido || "",
    segundoApellido: user.segundoApellido || "",
    dni: user.dni || "",
    telefono: user.telefono || "",
    email: user.email || "",
    password: "",
    role: user.role || "trabajador",
    empresa: user.empresa?._id || empresaId || "",
    grupoTrabajo: "",
    jornadas: Array.isArray(user.jornadas) ? user.jornadas.map(j=> typeof j === 'object' ? j._id : j) : [],
    imagenPerfil: null,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getEmpresas(token);
        setEmpresas(data);
        try { setGrupos(await getGruposTrabajo(token)); } catch(e){}
        try { setJornadasList(await getJornadas(token)); } catch(e){}
      } catch (err) {
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    // cuando cambia el prop user o empresaId, actualizar el form
    setForm((s) => {
      const newForm = {
        ...s,
        nombre: user.nombre || "",
        segundoNombre: user.segundoNombre || "",
        primerApellido: user.primerApellido || "",
        segundoApellido: user.segundoApellido || "",
        dni: user.dni || "",
        telefono: user.telefono || "",
        email: user.email || "",
        role: user.role || "trabajador",
        empresa: user.empresa?._id || empresaId || "",
        imagenPerfil: null,
      };

      // intentar detectar grupo del usuario (si user pertenece a algún grupo)
      try {
        const gruposMatch = (grupos || []).find(g => (g.empleados || []).map(String).includes(String(user._id)));
        if (gruposMatch) newForm.grupoTrabajo = gruposMatch._id;
      } catch (e) {}

      // Si el rol es global_admin, limpiar empresa
      if (newForm.role === "global_admin") {
        newForm.empresa = "";
      }

      return newForm;
    });
    // eslint-disable-next-line
  }, [user, empresaId]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Validaciones cliente
    if (!validarNombre(form.nombre.trim())) {
      setErrorMessage("Nombre inválido: solo letras, espacios, guiones y apóstrofes");
      return;
    }
    if (form.segundoNombre && !validarNombre(form.segundoNombre.trim())) {
      setErrorMessage("Segundo nombre inválido: solo letras, espacios, guiones y apóstrofes");
      return;
    }
    if (!validarNombre(form.primerApellido.trim())) {
      setErrorMessage("Primer apellido inválido: solo letras, espacios, guiones y apóstrofes");
      return;
    }
    if (form.segundoApellido && !validarNombre(form.segundoApellido.trim())) {
      setErrorMessage("Segundo apellido inválido: solo letras, espacios, guiones y apóstrofes");
      return;
    }
    if (!isValidDNI(form.dni.trim().toUpperCase())) {
      setErrorMessage("Documento inválido. Formatos aceptados: DNI (12345678A), NIE (X1234567A), Pasaporte (ABC123456) u otro documento (mínimo 5 caracteres)");
      return;
    }
    if (form.telefono && !isValidPhone(form.telefono.trim())) {
      setErrorMessage("Teléfono inválido: debe tener 9 dígitos (opcional)");
      return;
    }
    if (!validarEmail(form.email.trim())) {
      setErrorMessage("Email inválido: debe contener @ y tener terminacion .ejemplo");
      return;
    }
    if (!isEdit && !validarPassword(form.password)) {
      setErrorMessage("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (isEdit && form.password && !validarPassword(form.password)) {
      setErrorMessage("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    const fd = new FormData();
    fd.append("nombre", form.nombre.trim());
    if (form.segundoNombre) fd.append("segundoNombre", form.segundoNombre.trim());
    fd.append("primerApellido", form.primerApellido.trim());
    if (form.segundoApellido) fd.append("segundoApellido", form.segundoApellido.trim());
    fd.append("dni", form.dni.trim().toUpperCase());
    fd.append("telefono", form.telefono.trim());
    fd.append("email", form.email.trim());
    if (form.password) fd.append("password", form.password);
    fd.append("role", form.role);
    if (form.role !== "global_admin" && form.empresa) {
      fd.append("empresa", form.empresa);
    }
    if (form.grupoTrabajo) fd.append("grupoTrabajo", form.grupoTrabajo);
    if (form.jornadas && Array.isArray(form.jornadas)) {
      form.jornadas.forEach(j => fd.append("jornadas", j));
    }
    if (form.imagenPerfil) {
      fd.append("imagen", form.imagenPerfil);
    }

    // Si el creador es admin normal, forzar empresa
    if (me.role === "admin") {
      fd.set("empresa", me.empresa?._id || me.empresa);
    }

    // Si estamos en vista de empresa y hay empresaId, forzarlo
    if (empresaId) fd.set("empresa", empresaId);

    try {
      if (isEdit) {
        await updateUser(token, user._id, fd);
        
        // Si el usuario editado es el mismo que está logueado, recargar sus datos
        if (me && user._id === me._id) {
          await reloadUser();
        }
      } else {
        await createUser(token, fd);
      }

      onSaved && onSaved();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Error creando/actualizando usuario";
      setErrorMessage(msg);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4 md:p-6 rounded-t-xl">
          <div className="flex items-center gap-2 md:gap-3 text-white">
            <div className="w-10 h-10 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <i className="fas fa-user-edit text-xl md:text-2xl"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold">
              {isEdit ? "Editar usuario" : "Crear nuevo usuario"}
            </h3>
          </div>
        </div>

        {/* Contenido */}
        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-3 md:space-y-4">
          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 md:px-4 py-2 md:py-3 rounded-lg flex items-center gap-2">
              <i className="fas fa-exclamation-circle text-sm"></i>
              <span className="text-xs md:text-sm">{errorMessage}</span>
            </div>
          )}

          {/* Nombres */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-user text-gray-400 mr-2"></i>
                Nombre <span className="text-red-500">*</span>
              </label>
              <input
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Ej: Juan"
                required
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-user text-gray-400 mr-2"></i>
                Segundo Nombre
              </label>
              <input
                value={form.segundoNombre}
                onChange={(e) => setForm({ ...form, segundoNombre: e.target.value })}
                placeholder="Ej: Carlos"
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
              />
            </div>
          </div>

          {/* Apellidos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-user text-gray-400 mr-2"></i>
                Primer Apellido <span className="text-red-500">*</span>
              </label>
              <input
                value={form.primerApellido}
                onChange={(e) => setForm({ ...form, primerApellido: e.target.value })}
                placeholder="Ej: Pérez"
                required
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
              />
            </div>

            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-user text-gray-400 mr-2"></i>
                Segundo Apellido
              </label>
              <input
                value={form.segundoApellido}
                onChange={(e) => setForm({ ...form, segundoApellido: e.target.value })}
                placeholder="Ej: García"
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
              />
            </div>
          </div>

          {/* Documento y Teléfono */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-id-card text-gray-400 mr-2"></i>
                Documento de Identidad <span className="text-red-500">*</span>
              </label>
              <input
                value={form.dni}
                onChange={(e) => setForm({ ...form, dni: e.target.value.toUpperCase() })}
                placeholder="DNI/NIE/Pasaporte"
                maxLength="20"
                required
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base uppercase"
              />
              <p className="text-xs text-gray-500 mt-1">DNI: 12345678A | NIE: X1234567A</p>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-phone text-gray-400 mr-2"></i>
                Teléfono <span className="text-gray-400 text-xs">(opcional)</span>
              </label>
              <input
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="600123456"
                maxLength="9"
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
              <i className="fas fa-envelope text-gray-400 mr-2"></i>
              Email <span className="text-red-500">*</span>
            </label>
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="usuario@ejemplo.com"
              type="email"
              required
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
            />
          </div>

          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
              <i className="fas fa-lock text-gray-400 mr-2"></i>
              Contraseña {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <input
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              type="password"
              placeholder={isEdit ? "Dejar vacío para no cambiar" : "Mínimo 6 caracteres"}
              required={!isEdit}
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm md:text-base"
            />
            <p className="text-xs text-gray-500 mt-1">
              {isEdit ? "Solo si deseas cambiarla" : "Mínimo 6 caracteres"}
            </p>
          </div>

          {/* Empresa SOLO visible para global_admin SI NO ES global_admin el que se crea */}
          {me.role === "global_admin" && form.role !== "global_admin" && (
            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-building text-gray-400 mr-2"></i>
                Empresa <span className="text-red-500">*</span>
              </label>
              <select
                value={form.empresa}
                onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white text-sm md:text-base"
                required
              >
                <option value="">Selecciona una empresa</option>
                {empresas.map((e) => (
                  <option key={e._id} value={e._id}>
                    {e.nombre}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Empresa para admin - Solo muestra la suya (no editable) */}
          {me.role === "admin" && form.role !== "global_admin" && (
            <div>
              <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                <i className="fas fa-building text-gray-400 mr-2"></i>
                Empresa
              </label>
              <div className="w-full p-2.5 md:p-3 border border-gray-200 rounded-lg bg-gray-50 text-sm md:text-base text-gray-600">
                <i className="fas fa-lock text-gray-400 mr-2"></i>
                {me.empresa?.nombre || 'Tu empresa'}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Los usuarios que crees serán asignados automáticamente a tu empresa
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
              <i className="fas fa-user-tag text-gray-400 mr-2"></i>
              Rol
            </label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition bg-white text-sm md:text-base"
            >
              <option value="trabajador">👤 Trabajador</option>
              <option value="admin">🛡️ Admin empresa</option>
              {me.role === "global_admin" && (
                <option value="global_admin">👑 Global Admin</option>
              )}
            </select>
          </div>

          {/* Jornadas (opcional) - dropdown con checkboxes como en fichajes */}
          <div className="relative">
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
              <i className="fas fa-clock text-gray-400 mr-2"></i>
              Jornadas (opcional)
            </label>
            <button
              type="button"
              onClick={() => setJornadasDropdownOpen(!jornadasDropdownOpen)}
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-lg bg-white flex items-center justify-between"
            >
              <div className="flex items-center gap-2 text-sm md:text-base">
                <span>{Array.isArray(form.jornadas) && form.jornadas.length > 0 ? `${form.jornadas.length} seleccionada(s)` : 'Seleccionar jornadas'}</span>
              </div>
              <i className={`fas fa-chevron-${jornadasDropdownOpen ? 'up' : 'down'} text-gray-400`}></i>
            </button>

            {jornadasDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-300 rounded-lg shadow-xl z-10 w-full max-h-56 overflow-y-auto">
                <div className="p-2 border-b">
                  <button
                    type="button"
                    onClick={() => { setForm(prev => ({ ...prev, jornadas: [] })); setJornadasDropdownOpen(false); }}
                    className="text-sm text-gray-600 hover:text-gray-900 w-full text-left px-2 py-1"
                  >
                    Limpiar selección
                  </button>
                </div>
                {jornadasList.map((j) => (
                  <label key={j._id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Array.isArray(form.jornadas) && form.jornadas.includes(j._id)}
                      onChange={(e) => {
                        if (e.target.checked) setForm(prev => ({ ...prev, jornadas: Array.from(new Set([...(prev.jornadas||[]), j._id])) }));
                        else setForm(prev => ({ ...prev, jornadas: (prev.jornadas||[]).filter(id => id !== j._id) }));
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm md:text-base">{j.nombre}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
              <i className="fas fa-camera text-gray-400 mr-2"></i>
              Foto de perfil
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setForm({ ...form, imagenPerfil: e.target.files[0] })
              }
              className="w-full border border-gray-300 p-2.5 md:p-3 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition file:mr-2 md:file:mr-4 file:py-1.5 md:file:py-2 file:px-3 md:file:px-4 file:rounded-lg file:border-0 file:text-xs md:file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 text-sm md:text-base"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-4 md:pt-6 border-t border-gray-200">
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 md:px-6 py-2.5 md:py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition font-semibold shadow-md flex items-center justify-center gap-2 text-sm md:text-base"
            >
              <i className="fas fa-check"></i>
              {isEdit ? "Guardar cambios" : "Crear usuario"}
            </button>
            <button
              type="button"
              className="px-4 md:px-6 py-2.5 md:py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-semibold flex items-center justify-center gap-2 text-sm md:text-base"
              onClick={onClose}
            >
              <i className="fas fa-times"></i>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
